import { useMemo, useState } from 'react';
import { Stack, Typography, Button, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import StripedDataGrid from '../../../Shared/Components/Table/StripedDataGrid';
import { processedTimesheetDetailsTableColumns } from '../../../Shared/Utils/processedTimesheetDetailsTableColumns';
import ProcessedTimesheetLineDetailModal from './ProcessedTimesheetLineDetailModal';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import useDeleteProcessedTimeSheetEntry from '../../hooks/useDeleteProcessedTimesheetEntry';
import useToast from '../../../utils/useToast';
import { formatUtcDate } from '../../../utils/formatDate';

const ProcessedTimesheetDetailsTable = ({
    processedTimesheetId,
    data,
    isLoading,
    initialSortModel,
    availableRates,
    hideOpenButton = false,
    refetch,
    locations = [],
    readOnly = false,
}) => {
    const { renderToast, showSuccessToast, showErrorToast } = useToast();

    const [modalState, setModalState] = useState({
        open: false,
        editEntry: false,
        newEntry: false,
        selectedRow: null,
    });
    const [sortQueryParams, setSortQueryParams] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const {
        mutate: deleteProcessedTimesheetEntry,
        isLoading: deleteProcessedTimesheetEntryLoading,
    } = useDeleteProcessedTimeSheetEntry({
        onSuccess: () => {
            refetch();
            showSuccessToast('Entry deleted successfully');
            setShowDeleteConfirmation(false);
        },
        onError: (error) => {
            showErrorToast('There was an error deleting the entry. Please try again.');
            console.error('useDeleteProcessedTimesheetEntry:', error);
        },
    });

    const handleSortModelChange = (sortModel) => {
        const params = new URLSearchParams({
            sortField: sortModel[0]?.field || 'date',
            sortDirection: sortModel[0]?.sort || 'asc',
        }).toString();
        setSortQueryParams(params);
    };

    const sortedData = useMemo(
        () =>
            data?.slice().sort((a, b) => {
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            }),
        [data]
    );

    const handleView = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            editEntry: false,
            selectedRow: row,
        }));
    };

    const handleEdit = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            editEntry: true,
            selectedRow: row,
        }));
    };

    const handleDelete = (row) => {
        deleteProcessedTimesheetEntry({ id: row.id, isMileage: row.isMileage ?? false });
    };

    const handleClose = () => {
        setModalState((prevState) => ({
            ...prevState,
            open: false,
            editEntry: false,
            newEntry: false,
            selectedRow: null,
        }));
    };

    const openEmptyForm = () => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            newEntry: true,
            selectedRow: {},
        }));
    };

    const handleEditModeChange = () => {
        setModalState((prevState) => ({
            ...prevState,
            editEntry: true,
        }));
    };

    const handleDuplicate = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            open: true,
            newEntry: true,
            selectedRow: { ...row, id: undefined },
        }));
    };

    const setSelectedRow = (row) => {
        setModalState((prevState) => ({
            ...prevState,
            selectedRow: row,
        }));
    };

    return (
        <>
            {renderToast({ vertical: 'bottom', horizontal: 'center' })}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontWeight: 'bold', color: COLORS.LT_JOURNEY175 }}>
                    Processed Timesheet Details
                </Typography>
                <Stack direction="row">
                    {!readOnly && (
                        <Button
                            disableRipple
                            onClick={() => openEmptyForm()}
                            endIcon={<AddIcon sx={{ fontSize: '12px' }} />}
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: COLORS.LT_JOURNEY,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0,
                                padding: 0,
                                '& .MuiButton-endIcon': { margin: 0, marginLeft: '8px' },
                                marginRight: 2,
                            }}
                        >
                            New Item
                        </Button>
                    )}
                    {!hideOpenButton && (
                        <Button
                            href={`#/processed-timesheets/${processedTimesheetId}/details?${sortQueryParams}`}
                            target="_blank"
                            endIcon={<OpenInNewIcon sx={{ fontSize: '12px' }} />}
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: COLORS.LT_JOURNEY,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0,
                                padding: 0,
                                '& .MuiButton-endIcon': { margin: 0, marginLeft: '8px' },
                            }}
                            disableRipple
                        >
                            Open In New Tab
                        </Button>
                    )}
                </Stack>
            </Stack>
            <StripedDataGrid
                columns={processedTimesheetDetailsTableColumns}
                rows={sortedData || []}
                showOptions
                onView={handleView}
                onEdit={handleEdit}
                onDelete={(selectedRow) => {
                    setShowDeleteConfirmation(true);
                    setSelectedRow(selectedRow);
                }}
                isLoading={isLoading}
                initialSortModel={initialSortModel ?? [{ field: 'date', sort: 'asc' }]}
                onSortModelChange={handleSortModelChange}
                readOnly={readOnly}
            />
            <ProcessedTimesheetLineDetailModal
                isOpen={modalState.open}
                onClose={handleClose}
                editMode={modalState.editEntry}
                addMode={modalState.newEntry}
                data={modalState.selectedRow}
                onEditModeChange={handleEditModeChange}
                availableRates={availableRates}
                processedTimesheetId={processedTimesheetId}
                refetch={refetch}
                locations={locations}
                onDuplicate={handleDuplicate}
                readOnly={readOnly}
            />
            <Dialog
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                slotProps={{ paper: { sx: { borderRadius: '8px', padding: '24px' } } }}
            >
                <DialogContent>
                    <IconButton
                        disabled={false}
                        onClick={() => setShowDeleteConfirmation(false)}
                        sx={{ position: 'absolute', right: '10px', top: '5px' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Stack>
                        <Typography sx={{ color: COLORS.LT_FOUNDATION125 }}>
                            Are you sure you want to delete the {modalState?.selectedRow?.rateType}{' '}
                            detail from{' '}
                            {formatUtcDate(modalState?.selectedRow?.date, DATE_FORMATS.MD)}?
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                            color="secondary"
                            variant="contained"
                            sx={{ mr: 1 }}
                            onClick={() => setShowDeleteConfirmation(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="error"
                            onClick={() => handleDelete(modalState?.selectedRow)}
                            loading={deleteProcessedTimesheetEntryLoading}
                        >
                            Yes, delete detail
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ProcessedTimesheetDetailsTable;
